// src/components/Header.jsx
import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Header() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Load saved language on mount
  useEffect(() => {
    const savedLang = localStorage.getItem("site-lang") || "en";
    i18n.changeLanguage(savedLang);
  }, [i18n]);

  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "fr" : "en";
    i18n.changeLanguage(newLang);
    localStorage.setItem("site-lang", newLang);
  };

  const leftMenu = [
    { label: t("home"), href: "/" },
    { label: t("about"), href: "/about" },
    { label: t("projects"), href: "/projects"},
  ];

  const rightMenu = [
    { label: t("services"), href: "/services" },
    { label: t("gallery"), href: "/gallery" },
    { label: t("contact"), href: "/contact" },
    { label: t("how to buy"), href: "/how-to-buy" },
  ];

  const LinkItem = ({ item }) => {
    const isActive = !item.external && location.pathname === item.href;
    const Element = item.external ? "a" : Link;

    return (
      <Element
        to={!item.external ? item.href : undefined}
        href={item.external ? item.href : undefined}
        target={item.external ? "_blank" : undefined}
        rel={item.external ? "noopener noreferrer" : undefined}
        className={[
          "relative uppercase tracking-[0.22em] text-[11px] md:text-xs font-medium transition-colors duration-300 group",
          scrolled
            ? isActive
              ? "text-gray-900"
              : "text-gray-600 hover:text-black"
            : isActive
            ? "text-white"
            : "text-white/90 hover:text-white",
        ].join(" ")}
      >
        {item.label}
        <span
          className={[
            "absolute left-0 -bottom-1 h-[2px] transition-all duration-300",
            scrolled ? "bg-black" : "bg-white",
            isActive ? "w-full opacity-100" : "w-0 opacity-0",
          ].join(" ")}
        />
        <span
          className={`absolute left-0 -bottom-1 h-[2px] ${
            scrolled ? "bg-black" : "bg-white"
          } w-0 transition-all duration-300 group-hover:w-full`}
        />
      </Element>
    );
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={[
        "fixed top-0 left-0 w-full z-50 transition-all duration-300 ease-in-out shadow-sm",
        scrolled ? "bg-white h-16" : "bg-transparent h-20",
      ].join(" ")}
    >
      <div className="mx-auto max-w-[1200px] px-4 md:px-6 h-full">
        {/* Desktop */}
        <div className="hidden md:grid grid-cols-[1fr_auto_1fr] items-center h-full">
          <nav className="justify-self-start">
            <ul className="flex items-center gap-8">
              {leftMenu.map((it) => (
                <li key={it.label}>
                  <LinkItem item={it} />
                </li>
              ))}

              {/* Properties Dropdown (Desktop - Click Toggle) */}
              <li>
                <LinkItem
                  item={{
                    label: "Virtual Tour",
                    href: "/virtual-tour",
                  }}
                />
              </li>
            </ul>
          </nav>

          {/* Logo */}
          <Link
            to="/"
            className="justify-self-center transition-transform duration-300"
          >
            <img
              src="/logo.png"
              alt="Aspen Plush Homes"
              className={`transition-transform duration-300 ${
                scrolled ? "h-7" : "h-9"
              } w-auto`}
            />
          </Link>

          {/* Right menu + language switch */}
          <nav className="justify-self-end flex items-center gap-6">
            <ul className="flex items-center gap-8">
              {rightMenu.map((it) => (
                <li key={it.label}>
                  <LinkItem item={it} />
                </li>
              ))}
            </ul>
            <button
              onClick={toggleLanguage}
              className="uppercase bg-blue-400 text-white px-3 py-1 rounded-md text-[11px] font-medium tracking-[0.22em] hover:bg-blue-500 transition"
            >
              {i18n.language.toUpperCase()}
            </button>
          </nav>
        </div>

        {/* Mobile */}
        <div className="md:hidden flex items-center justify-between h-full">
          <div className="w-10" />
          <Link to="/" className="inline-flex">
            <img
              src="/logo.png"
              alt="Aspen Plush Homes"
              className={`transition-transform duration-300 ${
                scrolled ? "h-6" : "h-8"
              } w-auto`}
            />
          </Link>
          <button
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            className={`w-10 h-10 grid place-items-center ${
              scrolled
                ? "text-gray-700 hover:text-black"
                : "text-white/90 hover:text-white"
            }`}
          >
            <Menu />
          </button>
        </div>
      </div>

      {/* Mobile Overlay */}
      {open && (
        <div
          className={`fixed inset-0 z-50 md:hidden transition-colors duration-500 ${
            scrolled
              ? "bg-white/95 backdrop-blur-md"
              : "bg-black/90 backdrop-blur-md"
          }`}
        >
          <div className="flex items-center justify-between px-4 h-16">
            <div className="w-10" />
            <Link to="/" className="inline-flex" onClick={() => setOpen(false)}>
              <img src="/logo.png" alt="Aspen Plush Homes" className="h-8" />
            </Link>
            <button
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              className={`w-10 h-10 grid place-items-center ${
                scrolled
                  ? "text-gray-700 hover:text-black"
                  : "text-white/90 hover:text-white"
              }`}
            >
              <X />
            </button>
          </div>

          <div className="px-6 pt-8 overflow-y-auto h-[calc(100%-4rem)]">
            <ul className="space-y-6">
              {leftMenu.map((it) => (
                <li key={it.label}>
                  <Link
                    to={it.href}
                    onClick={() => setOpen(false)}
                    className={`block text-lg ${
                      scrolled
                        ? "text-gray-700 hover:text-black"
                        : "text-white/90 hover:text-white"
                    }`}
                  >
                    {it.label}
                  </Link>
                </li>
              ))}

              <li>
                <Link
                  to="/virtual-tour"
                  onClick={() => setOpen(false)}
                  className={`block text-lg ${
                    scrolled
                      ? "text-gray-700 hover:text-black"
                      : "text-white/90 hover:text-white"
                  }`}
                >
                  Virtual Tour
                </Link>
              </li>

              {rightMenu.map((it) => (
                <li key={it.label}>
                  <Link
                    to={it.href}
                    onClick={() => setOpen(false)}
                    className={`block text-lg ${
                      scrolled
                        ? "text-gray-700 hover:text-black"
                        : "text-white/90 hover:text-white"
                    }`}
                  >
                    {it.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Language switch (mobile) */}
            <div className="mt-8">
              <button
                onClick={toggleLanguage}
                className="uppercase bg-blue-400 text-white px-4 py-2 rounded-md text-sm font-medium tracking-[0.22em] hover:bg-blue-500 transition"
              >
                {i18n.language.toUpperCase()}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
